import {
  collection,
  doc,
  getDoc as _getDoc,
  getDocs as _getDocs,
  setDoc as _setDoc,
  updateDoc as _updateDoc,
  deleteDoc as _deleteDoc,
  FieldPath,
  onSnapshot,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  type DocumentData,
  type DocumentReference,
  type FirestoreDataConverter,
  type Query,
  type QueryDocumentSnapshot,
  type SnapshotOptions,
  type QueryConstraint,
  type SetOptions,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import { COLLECTIONS } from '@/config/constants';
import { networkStats } from '@/lib/network-stats';
import { enforceRateLimit } from '@/lib/rate-guard';

// ── Firestore refresh event bus (for offline persistence — currently disabled) ──
// TODO: Re-enable when persistentLocalCache is active. When getDoc/getDocs use
// onSnapshot internally, this bus notifies stores of server data arriving after cache.

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RefreshHandler = (path: string, data: Record<string, any> | null) => void;
const refreshHandlers = new Set<RefreshHandler>();

/** Subscribe to Firestore cache-refresh events. Returns unsubscribe function. */
export function onFirestoreRefresh(handler: RefreshHandler): () => void {
  refreshHandlers.add(handler);
  return () => { refreshHandlers.delete(handler); };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function emitRefresh(path: string, data: Record<string, any> | null) {
  for (const handler of refreshHandlers) {
    try { handler(path, data); } catch (e) { console.error('[Firestore] refresh handler error:', e); }
  }
}


// ── Tracked wrappers — automatically count + log Firestore reads ──

/** Extract a human-readable path from a Query (best-effort, uses SDK internals) */
function getQueryPath(q: Query<unknown>): string {
  try {
    // CollectionReference extends Query and exposes .path
    if ('path' in q && typeof (q as Record<string, unknown>).path === 'string')
      return (q as Record<string, unknown>).path as string;
    // Firebase v9+ internal: Query._query.path.segments
    const segments = (q as Record<string, unknown> & { _query?: { path?: { segments?: string[] } } })
      ?._query?.path?.segments;
    if (Array.isArray(segments)) return segments.join('/');
  } catch { /* best-effort */ }
  return '<query>';
}

/**
 * Tracked `getDoc` — cache-first via onSnapshot.
 *
 * With persistentLocalCache, onSnapshot fires instantly from IndexedDB cache.
 * When server data arrives and differs, emits a refresh event on the bus
 * so subscribed stores can update without a second Firestore call.
 */
export function getDoc<T extends DocumentData>(ref: DocumentReference<T>) {
  enforceRateLimit(`getDoc:${ref.path}`, `getDoc ${ref.path}`);
  networkStats.firestoreReadCall();
  return new Promise<Awaited<ReturnType<typeof _getDoc<T>>>>((resolve, reject) => {
    let resolved = false;
    let cachedJson: string | undefined;
    const unsub = onSnapshot(ref, { includeMetadataChanges: true }, (snap) => {
      const count = snap.exists() ? 1 : 0;
      const source = snap.metadata.fromCache ? 'cache' : 'server';
      if (!resolved) {
        resolved = true;
        cachedJson = snap.exists() ? JSON.stringify(snap.data()) : undefined;
        networkStats.firestoreRead(count);
        console.log(`[Firestore] getDoc ${ref.path} → ${count} doc (${source})`);
        resolve(snap);
        if (!snap.metadata.fromCache) unsub();
      } else {
        if (snap.metadata.fromCache) return; // Skip intermediate cache events
        const serverData = snap.exists() ? (snap.data() as Record<string, unknown>) : null;
        const serverJson = serverData ? JSON.stringify(serverData) : undefined;
        if (serverJson !== cachedJson) {
          console.log(`[Firestore] getDoc ${ref.path} → ${count} doc (refresh)`);
          emitRefresh(ref.path, serverData);
        }
        unsub();
      }
    }, (error) => {
      if (!resolved) { resolved = true; reject(error); }
      unsub();
    });
  });
}

/**
 * Tracked `getDocs` — cache-first via onSnapshot.
 *
 * Uses docChanges() to emit refresh only for docs that actually changed.
 */
export function getDocs<T extends DocumentData>(q: Query<T>) {
  const path = getQueryPath(q);
  enforceRateLimit(`getDocs:${path}`, `getDocs ${path}`);
  networkStats.firestoreReadCall();
  return new Promise<Awaited<ReturnType<typeof _getDocs<T>>>>((resolve, reject) => {
    let resolved = false;
    const unsub = onSnapshot(q, { includeMetadataChanges: true }, (snap) => {
      const source = snap.metadata.fromCache ? 'cache' : 'server';
      if (!resolved) {
        resolved = true;
        networkStats.firestoreRead(snap.docs.length);
        console.log(`[Firestore] getDocs ${path} → ${snap.docs.length} docs (${source})`);
        resolve(snap);
        if (!snap.metadata.fromCache) unsub();
      } else {
        if (snap.metadata.fromCache) return; // Skip intermediate cache events
        // Server data arrived — emit refresh for all docs
        console.log(`[Firestore] getDocs ${path} → server sync: ${snap.docs.length} docs`);
        for (const d of snap.docs) {
          emitRefresh(`${path}/${d.id}`, d.data() as Record<string, unknown>);
        }
        unsub();
      }
    }, (error) => {
      if (!resolved) { resolved = true; reject(error); }
      unsub();
    });
  });
}

/** Tracked `setDoc` — increments network stats write counter + logs path */
export async function setDoc<T extends DocumentData>(ref: DocumentReference<T>, data: Partial<T>, options?: SetOptions) {
  enforceRateLimit(`setDoc:${ref.path}`, `setDoc ${ref.path}`);
  if (options) {
    await _setDoc(ref, data as T, options);
  } else {
    await _setDoc(ref, data as T);
  }
  networkStats.firestoreWrite();
  console.log(`[Firestore] setDoc ${ref.path}`);
}

/** Tracked `updateDoc` — increments network stats write counter + logs path.
 *  Two forms, mirroring the Firebase SDK:
 *   - object form:     updateDoc(ref, { 'a.b': value })   — dots are split into a nested path
 *   - field-path form: updateDoc(ref, new FieldPath('a', b), value)
 *  Use the field-path form when a map key may contain '.' (or other field-path
 *  metacharacters) so it is treated as ONE literal segment instead of a path. */
export async function updateDoc<T extends DocumentData>(ref: DocumentReference<T>, data: Partial<T>): Promise<void>;
export async function updateDoc<T extends DocumentData>(ref: DocumentReference<T>, field: string | FieldPath, value: unknown, ...moreFieldsAndValues: unknown[]): Promise<void>;
export async function updateDoc<T extends DocumentData>(
  ref: DocumentReference<T>,
  dataOrField: Partial<T> | string | FieldPath,
  value?: unknown,
  ...moreFieldsAndValues: unknown[]
) {
  enforceRateLimit(`updateDoc:${ref.path}`, `updateDoc ${ref.path}`);
  if (typeof dataOrField === 'string' || dataOrField instanceof FieldPath) {
    await _updateDoc(ref, dataOrField, value, ...moreFieldsAndValues);
  } else {
    await _updateDoc(ref, dataOrField as DocumentData);
  }
  networkStats.firestoreWrite();
  console.log(`[Firestore] updateDoc ${ref.path}`);
}

/** Tracked `deleteDoc` — increments network stats write counter + logs path */
export async function deleteDoc<T extends DocumentData>(ref: DocumentReference<T>) {
  enforceRateLimit(`deleteDoc:${ref.path}`, `deleteDoc ${ref.path}`);
  await _deleteDoc(ref);
  networkStats.firestoreWrite();
  console.log(`[Firestore] deleteDoc ${ref.path}`);
}

// Generic typed converter factory
function createConverter<T extends DocumentData>(): FirestoreDataConverter<T> {
  return {
    toFirestore: (data: T) => data,
    fromFirestore: (snap: QueryDocumentSnapshot, options: SnapshotOptions) =>
      ({ id: snap.id, ...snap.data(options) }) as unknown as T,
  };
}

// Typed collection references
export const usersRef = () =>
  collection(db, COLLECTIONS.USERS).withConverter(createConverter());

export const userDoc = (uid: string) =>
  doc(db, COLLECTIONS.USERS, uid).withConverter(createConverter());

export const userPrivateProfile = (uid: string) =>
  doc(db, COLLECTIONS.USERS, uid, 'private', 'profile');

export const appPrefsDoc = (saas: string, orgId: string, appCode: string) =>
  doc(db, COLLECTIONS.APP_PREFERENCES, `${saas}_orgs`, orgId, appCode);

export const creditsDoc = (uid: string) =>
  doc(db, COLLECTIONS.USER_CREDITS, uid);

export const analyticsCollection = (uid: string, appCode: string) =>
  collection(db, COLLECTIONS.USER_STATS, uid, appCode);

export const licenseOwnersDoc = (uid: string) =>
  doc(db, COLLECTIONS.LICENSE_OWNERS, uid);

export const subscriptionsCollection = (uid: string) =>
  collection(db, COLLECTIONS.CUSTOMERS, uid, 'subscriptions');

export const errorLogsCollection = () =>
  collection(db, COLLECTIONS.ERROR_LOGS);

export const notificationDataDoc = (extension: string, notifId: string) =>
  doc(db, COLLECTIONS.NOTIFICATION_DATA, extension, 'notificationDataMap', notifId);

// Generic helpers — use tracked getDoc/getDocs so every read is counted + logged.
export async function getDocument<T = DocumentData>(path: string, ...pathSegments: string[]) {
  const ref = doc(db, path, ...pathSegments);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as T;
}

export async function queryDocuments<T = DocumentData>(
  collectionPath: string,
  constraints: QueryConstraint[] = []
) {
  const ref = collection(db, collectionPath);
  const q = query(ref, ...constraints);
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as T);
}

export async function setDocument(path: string, id: string, data: DocumentData) {
  const ref = doc(db, path, id);
  await setDoc(ref, data, { merge: true });
}

export async function updateDocument(path: string, id: string, data: Partial<DocumentData>) {
  const ref = doc(db, path, id);
  await updateDoc(ref, data);
}

export async function deleteDocument(path: string, id: string) {
  const ref = doc(db, path, id);
  await deleteDoc(ref);
}

export function subscribeToDocument(
  path: string,
  id: string,
  callback: (data: DocumentData | null) => void
) {
  const ref = doc(db, path, id);
  return onSnapshot(ref, (snap) => {
    callback(snap.exists() ? { id: snap.id, ...snap.data() } : null);
  });
}

export function subscribeToCollection(
  collectionPath: string,
  constraints: QueryConstraint[],
  callback: (data: DocumentData[]) => void
) {
  const ref = collection(db, collectionPath);
  const q = query(ref, ...constraints);
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
}

// Re-export commonly used Firestore functions
// NOTE: getDoc/getDocs/setDoc/updateDoc/deleteDoc are tracked wrappers defined above —
// do NOT re-export the raw Firebase ones (_getDoc, _getDocs, _setDoc, _updateDoc, _deleteDoc)
export { where, orderBy, limit, startAfter, query, collection, doc, onSnapshot };

// Pass-through re-exports for callers that need the cache-bypassing /
// auto-id variants. These are not tracked through the wrapper above —
// the e2e mock module overrides them with in-memory equivalents.
// Server-only reads. The cache-first helpers above answer from an empty cache
// with "nothing here", which is indistinguishable from "not fetched yet" — so
// anything that must not miss a just-written document reads through these.
export { getDocFromServer, getDocsFromServer, addDoc } from 'firebase/firestore';
// Aggregation count — answers "how many?" without downloading the documents.
// One billed read per 1000 index entries, always served by the server.
export { getCountFromServer } from 'firebase/firestore';
// Atomic multi-doc writes. Callers MUST import this through the wrapper (not
// 'firebase/firestore' directly) so the e2e alias swaps in the in-memory
// stub — a direct import commits against the real `db` under VITE_E2E.
export { writeBatch } from 'firebase/firestore';
